import { customSanitize } from "../../utils/customSanitize";
import { optimizeURL } from "../../utils/url";
import { Date as DateComponent } from "@aleksundshantu/aunds-utils-react/components";
import styles from "./PostCard.module.scss";
import { ImageType } from "../../types/baseTypes";
import { WP_Term } from "wp-types";
import { ContentTag } from "../ContentTag/ContentTag";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { Button } from "../Button/Button";
import { MediaItem } from "../MediaItem/MediaItem";
import { CustomLink } from "../CustomLink";
import { CategoryOverviewPages } from "@/getData/getIndexedPostGridData";

export type PostsGridElementType = {
  id: number;
  date: string;
  title: string;
  link: string;
  image: ImageType | false;
  excerpt: string;
  categories: WP_Term[];
  media_format?: MediaFormatType;
  content: string;
};

interface PostCardProps {
  element: PostsGridElementType;
  readMoreLabel?: string;
  border_radius: boolean;
  box_shadow: boolean;
  showBox: boolean;
  grid_columns: "two-cols" | "three-cols";
  media_format?: MediaFormatType;
  skeleton?: boolean;
  allCategories?: CategoryOverviewPages[];
  isSlider?: boolean;
}

const PostCard = ({
  element,
  readMoreLabel = "Read more",
  border_radius,
  box_shadow,
  showBox,
  grid_columns = "two-cols",
  media_format,
  skeleton,
  allCategories,
  isSlider = false,
}: PostCardProps) => {
  const { categories, excerpt, image, link, title, content } = element;
  const { getLabel } = useGlobalContext();
  const filteredCategories = categories.filter(
    (el) => el.name !== "Unkategorisiert",
  );

  // Todo: Remove this when postsslider have media_format set
  const defaultMediaFormat: MediaFormatType = {
    mobile: "square",
    tablet: "square",
    desktop: "square",
    fullhd: "square",
  };

  const optimizedURL = optimizeURL(link);
  const skeletonElementClass = skeleton ? "skeleton-element" : "";
  return (
    <article
      className={`${styles["embla-slide"]} ${styles["slider-element"]} ${isSlider ? styles["is-slider"] : ""} ${styles[grid_columns]} ${box_shadow && showBox ? "has-box-shadow" : ""} ${border_radius && showBox ? "has-border-radius" : ""} is-flex ${styles[image ? "has-image" : "no-image"]}`}
    >
      <span className={`date ${skeletonElementClass}`}>
        <DateComponent
          date={new Date(element.date)}
          dateFormat={"dd. MMM yyyy"}
        />
      </span>
      <section
        className={`${styles["info-wrapper"]} ${!image ? styles["no-image"] : ""} is-flex is-flex-direction-column ${showBox ? "is-box" : ""}`}
      >
        <header>
          <p
            className={`${styles["info-top"]} ${!image ? styles["no-image"] : ""}`}
          >
            {filteredCategories.length > 0 &&
              filteredCategories.map((category, index) => {
                const categoryLink = (allCategories || []).find(
                  (el) => el.slug === category.slug,
                )?.link;

                const content = (
                  <>
                    <span className={`category mr-2 ${skeletonElementClass}`}>
                      {category.name.replaceAll("&amp;", "&")}
                    </span>
                    {index < filteredCategories.length - 1 && (
                      <span className={styles.separator + " category"}>|</span>
                    )}
                  </>
                );

                return categoryLink ? (
                  <CustomLink
                    className={styles["disable-hover"]}
                    key={index}
                    href={categoryLink}
                    target="_self"
                  >
                    {content}
                  </CustomLink>
                ) : (
                  <span key={index} className={styles["disable-hover"]}>
                    {content}
                  </span>
                );
              })}
          </p>
          {title && (
            <p
              role="heading"
              aria-level={3}
              className={`${styles.title} title pb-0 mb-4 ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(title),
              }}
            />
          )}
          {!image && excerpt && (
            <ContentTag
              content={excerpt}
              extraClass={`${styles["content"]} pb-0 mb-4 ${skeletonElementClass}`}
            />
          )}
          {!image && !excerpt && content && (
            <ContentTag
              content={content}
              extraClass={`${styles["content"]} pb-0 mb-4 ${skeletonElementClass}`}
            />
          )}
        </header>

        {link && (
          <footer className="mt-auto">
            <Button
              type="secondary"
              href={optimizedURL!}
              target="_self"
              className={`my-0 pb-0 ${skeletonElementClass}`}
              aria-label={getLabel("aria_label_postlink").replace(
                "$posttitle",
                title,
              )}
              disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
            >
              {readMoreLabel}
            </Button>
          </footer>
        )}
      </section>
      {image && (
        <MediaItem
          hasOverlay={false}
          image_desktop={image}
          className={`${skeletonElementClass} ${image?.url.endsWith(".png") ? "p-3" : ""}`}
          mediaFormat={media_format ? media_format : defaultMediaFormat}
          sizes="(max-width: 768px) 85vw, (max-width: 1599px) 20vw, 320px"
          link={
            optimizedURL
              ? { url: optimizedURL, target: "_self", title: "" }
              : undefined
          }
          linkProps={{ tabIndex: -1 }}
          disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
          objectFit={image?.url.endsWith(".png") ? "contain" : "cover"}
        />
      )}
    </article>
  );
};

export default PostCard;
